import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { usersApi, clinicsApi } from '../../lib/api';
import { toast } from 'sonner';
import { Stethoscope, Search, ChevronRight, Mail, Phone, Building2, UserPlus } from 'lucide-react';

export const DoctorsPage = () => {
  const [doctors, setDoctors] = useState([]);
  const [clinicsById, setClinicsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetch = async () => {
      try {
        const [doctorsRes, clinicsRes] = await Promise.all([
          usersApi.list({ role: 'doctor', limit: 100 }),
          clinicsApi.list(),
        ]);
        setDoctors(doctorsRes.users || []);
        const map = {};
        (clinicsRes.clinics || []).forEach((c) => {
          map[c.id] = c.name;
        });
        setClinicsById(map);
      } catch (err) {
        toast.error('Doktorlar yüklenemedi');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const filtered = doctors.filter((d) => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    return (
      (d.name || '').toLowerCase().includes(q) ||
      (d.email || '').toLowerCase().includes(q)
    );
  });

  return (
    <DashboardLayout title="Doktorlar" subtitle="Sistemdeki tüm doktorlar">
      <div className="space-y-4" data-testid="doctors-page">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                placeholder="İsim veya e-posta ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="doctors-search-input"
              />
            </div>
            <Link
              to="/credit-admin/doctors"
              className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-lg bg-[#5A67D8] hover:bg-[#4338CA] text-white text-sm font-medium transition-colors"
              data-testid="superadmin-doctors-add-btn"
            >
              <UserPlus className="w-4 h-4" />
              Doktor Ekle
            </Link>
          </CardContent>
        </Card>

        {loading ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-8 text-center text-slate-400">Yükleniyor...</CardContent>
          </Card>
        ) : filtered.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-8 text-center text-slate-400" data-testid="doctors-empty">
              {doctors.length === 0 ? 'Henüz doktor yok' : 'Eşleşen doktor bulunamadı'}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {filtered.map((d) => (
              <Link
                key={d.id}
                to={`/superadmin/doctors/${d.id}`}
                className="block group"
                data-testid={`doctor-row-${d.id}`}
              >
                <Card className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#5A67D8] to-[#4C51BF] flex items-center justify-center flex-shrink-0 text-white">
                      <Stethoscope className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-slate-800 truncate">{d.name || '—'}</p>
                        {!d.is_active && <Badge variant="secondary" className="text-xs">Pasif</Badge>}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-slate-500 mt-1 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {d.email}
                        </span>
                        {d.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {d.phone}
                          </span>
                        )}
                        {d.clinic_id && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" /> {clinicsById[d.clinic_id] || '—'}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-[#5A67D8] transition-colors" />
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DoctorsPage;
